import axios from "axios";
import Papa from "papaparse";
import { parseFile } from "./helpers/file-parser";
import { mapTag } from "./helpers/tag-mapper";
import { mapStateToImportConfig } from "./helpers/import-mapper";
import { ImportUploadStatus } from "../const/import-status";
import { ImportFieldType, SUBSCRIPTION_STATUS_FIELDS } from "../const/field-type";
import { SubscriptionSource } from "../const/subscription-source";

const RAW_NEGATIVE_VALUES = new Set([
  "0",
  "no",
  "n",
  "false",
  "unsubscribed",
  "unsub",
  "optout",
  "opt-out",
  "wypisany",
  "wypisana",
  "nie",
  "não",
  "nao",
  "falso",
  "cancelado",
  "cancelada",
  "descadastrado",
  "descadastrada",
  "baja",
  "dado de baja",
  "dada de baja",
  "no suscrito",
  "no suscrita",
]);

function isExcelFile(file) {
  return (
    file.name.endsWith(".xlsx") ||
    file.name.endsWith(".xls") ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

function scanRowsForNegativeValues(rows, skipFirstLine, subscriptionColumnIndices) {
  const startIndex = skipFirstLine ? 1 : 0;
  for (let i = startIndex; i < rows.length; i++) {
    const row = rows[i];
    for (const colIndex of subscriptionColumnIndices) {
      if (colIndex < row.length) {
        const value = (row[colIndex] || "").toString().trim().toLowerCase();
        if (RAW_NEGATIVE_VALUES.has(value)) {
          return true;
        }
      }
    }
  }
  return false;
}

function scanExcelFileForNegativeValues(file, skipFirstLine, subscriptionColumnIndices) {
  return new Promise((resolve) => {
    if (!window.Worker) {
      resolve(false);
      return;
    }
    const worker = new Worker("/assets/js/import-v2/xlsx.worker.js");
    worker.postMessage(file);
    worker.onmessage = (e) => {
      const rows = e.data;
      resolve(scanRowsForNegativeValues(rows, skipFirstLine, subscriptionColumnIndices));
      worker.terminate();
    };
    worker.onerror = () => {
      resolve(false);
      worker.terminate();
    };
  });
}

function scanCsvFileForNegativeValues(file, skipFirstLine, subscriptionColumnIndices) {
  return new Promise((resolve) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data || [];
        const hasNegative = scanRowsForNegativeValues(rows, skipFirstLine, subscriptionColumnIndices);
        resolve(hasNegative);
      },
      error: () => resolve(false),
    });
  });
}

function scanFileForNegativeValues(file, skipFirstLine, subscriptionColumnIndices) {
  if (isExcelFile(file)) {
    return scanExcelFileForNegativeValues(file, skipFirstLine, subscriptionColumnIndices);
  }
  return scanCsvFileForNegativeValues(file, skipFirstLine, subscriptionColumnIndices);
}

function determineSubscriptionSource(selectedFields) {
  const hasAnySubscriptionMapped = selectedFields.some((field) => SUBSCRIPTION_STATUS_FIELDS.includes(field));
  return hasAnySubscriptionMapped ? SubscriptionSource.File : SubscriptionSource.NoUpdate;
}

function runDemoUpload(commit) {
  setTimeout(() => {
    commit("setUploadProgress", 70);
  }, 1000);
  setTimeout(() => {
    commit("setUploadProgress", 100);
    commit("setImportId", new Date().getTime());
    commit("setImportStatus", ImportUploadStatus.Success);
  }, 3000);
}

export const importActions = {
  setDefaultState({ commit }) {
    commit("setDefaultState");
  },
  setFileToUpload({ state, commit }, file) {
    commit("setMappingPreviewLoading", true);
    commit("setAiProcessed", false);
    commit("setValidatedFileChecksum", null);
    commit("setMappingSuggestionError", false);
    commit("setHasNegativeSubscriptionValues", false);
    commit("setLastNegativeDetectionMappingHash", null);
    commit("setPreviewRows", []);
    parseFile(
      {
        file,
        delimiter: state.delimiter,
      },
      (result) => {
        commit("setMappingPreviewLoading", false);
        commit("setImportType", result.type);
        if (result.delimiter) {
          commit("setDelimiter", result.delimiter);
        }
        commit("setPreviewRows", result.previewRows);
        commit("setNoOfRows", result.noOfRows);
        commit("setSkipFirstLine", result.skipFirstLine);
      }
    );

    commit("setFileToUpload", file);
  },
  fetchTags({ state, commit }) {
    if (state.tags.importTags.length > 0) {
      return;
    }

    commit("setTagsLoading", true);
    return axios
      .get("/json/settings/import/tags")
      .then(({ data: { tagList } }) => {
        commit("setTags", tagList.filter((t) => !t.isDeleted).map(mapTag));
      })
      .finally(() => {
        commit("setTagsLoading", false);
      });
  },
  setFieldMapping({ commit, state }, { colIndex, fieldMapping }) {
    commit("setFieldMapping", { colIndex, fieldMapping });
    const newSource = determineSubscriptionSource(state.fieldMappings.selectedFields);
    if (state.subscriptions.source !== newSource) {
      commit("setSubscriptionSource", newSource);
    }
  },
  toggleTag({ commit }, id) {
    commit("updateTagSelection", id);
  },
  setTagsSelection({ commit }, selectedIds) {
    commit("setTagsSelection", selectedIds);
  },
  addTag({ state, commit }) {
    axios
      .put("/tags", {
        tag_name: state.tags.newTagName,
      })
      .then(({ data }) => {
        if (!data) {
          window.showToast("Cannot create new tag - try again in a while", "error");
        } else {
          commit("addTag", Object.assign(mapTag(data), { isSelected: true }));
          commit("updateNewTagName", "");
          window.showToast("New tag has been created", "success");
        }
      });
  },
  loadImportHistory({ commit }) {
    axios
      .post("/json/settings/getImportHistory")
      .then(
        ({
          data: {
            data: { dataImportHistory },
          },
        }) => {
          commit("setImportHistory", dataImportHistory);
        }
      )
      .catch(() => {
        window.showToast("Cannot fetch import history - try again later", "warn");
      });
  },
  loadImportStatistics({ commit }, dataImportId) {
    axios
      .get("/json/settings/import/statistics", {
        params: {
          dataImportId: dataImportId,
        },
      })
      .then((request) => {
        commit("setImportStatistics", {
          dataImportId: dataImportId,
          statistics: request.data,
        });
      });
  },
  loadImportErrors({ commit }, dataImportId) {
    axios.post("/json/settings/get_single_history", { dataImportId: dataImportId }).then((request) => {
      commit("setImportErrors", {
        dataImportId: dataImportId,
        errors: request.data.data.dataImportErrors,
      });
    });
  },
  async prepareImport({ commit, state, rootGetters }) {
    if (!state.fileToUpload) {
      return;
    }

    const canRetry = state.importStatus === ImportUploadStatus.Error;
    if (state.importId && !canRetry) {
      return;
    }

    if (canRetry) {
      commit("setImportId", null);
      commit("setImportStatus", ImportUploadStatus.Default);
    }

    if (rootGetters["global/isDemo"]) {
      commit("setImportId", String(new Date().getTime()));
      return;
    }

    commit("setImportPreparing", true);

    const formData = new FormData();
    formData.append("file", state.fileToUpload);
    formData.append("checksum", state.checksum);
    formData.append("aiProcessed", state.fieldMappings.aiProcessed ? "1" : "0");
    const firstDataRowIndex = state.skipFirstLine ? 1 : 0;
    if (state.fieldMappings.previewRows.length > firstDataRowIndex) {
      formData.append("firstLines", state.fieldMappings.previewRows[firstDataRowIndex].join(state.delimiter));
    }

    try {
      const response = await axios.post("/settings/import_file", formData, {
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          commit("setUploadProgress", progress);
        },
      });

      const { status, mappingResponse } = response.data.data;
      if (status === "success") {
        commit("setImportId", mappingResponse.dataImportId);
      } else {
        throw new Error(mappingResponse || "Upload failed");
      }
    } finally {
      commit("setImportPreparing", false);
    }
  },
  async uploadFile({ commit, dispatch, state, rootGetters }) {
    await dispatch("prepareImport");

    if (!state.importId) {
      commit("setImportStatus", ImportUploadStatus.Error);
      return;
    }

    commit("setImportStatus", ImportUploadStatus.Processing);

    if (rootGetters["global/isDemo"]) {
      runDemoUpload(commit);
      return;
    }

    try {
      await axios.post("/json/settings/submit_mapping", mapStateToImportConfig(state));
      commit("setImportStatus", ImportUploadStatus.Success);
    } catch {
      commit("setImportStatus", ImportUploadStatus.Error);
    }
  },
  updateNewTagName({ commit }, tagName) {
    commit("updateNewTagName", tagName);
  },
  setSubscriptionSource({ commit, state }, source) {
    commit("setSubscriptionSource", source);
    if (source === "manual") {
      if (state.subscriptions.email === null) {
        commit("setSubscriptionStatusEmail", "unknown");
      }
      if (state.subscriptions.sms === null) {
        commit("setSubscriptionStatusSms", "unknown");
      }
      if (state.subscriptions.whatsapp === null) {
        commit("setSubscriptionStatusWhatsapp", "unknown");
      }
    }
  },
  setTreatNegativeStatusAsUnknown({ commit }, value) {
    commit("setTreatNegativeStatusAsUnknown", value);
  },
  setSubscriptionStatusEmail({ commit }, status) {
    commit("setSubscriptionStatusEmail", status);
  },
  setSubscriptionStatusSms({ commit }, status) {
    commit("setSubscriptionStatusSms", status);
  },
  setSubscriptionStatusWhatsapp({ commit }, status) {
    commit("setSubscriptionStatusWhatsapp", status);
  },
  setDuplicatesAction({ commit }, action) {
    commit("setDuplicatesAction", action);
  },
  setDelimiter({ commit }, delimiter) {
    commit("setDelimiter", delimiter);
  },
  setSkipFirstLine({ commit }, skipFirstLine) {
    commit("setSkipFirstLine", skipFirstLine);
  },
  setTagsAction({ commit }, action) {
    commit("setTagsAction", action);
  },
  async detectNegativeSubscriptionValues({ commit, state }) {
    const subscriptionColumnIndices = state.fieldMappings.selectedFields
      .map((field, index) => (SUBSCRIPTION_STATUS_FIELDS.includes(field) ? index : -1))
      .filter((index) => index !== -1);

    if (subscriptionColumnIndices.length === 0) {
      commit("setHasNegativeSubscriptionValues", false);
      return;
    }

    const currentMappingHash = JSON.stringify({
      selectedFields: state.fieldMappings.selectedFields,
      skipFirstLine: state.skipFirstLine,
      fileChecksum: state.checksum,
    });

    if (state.lastNegativeDetectionMappingHash === currentMappingHash) {
      return;
    }

    let hasNegative = false;

    if (state.fileToUpload) {
      try {
        hasNegative = await scanFileForNegativeValues(
          state.fileToUpload,
          state.skipFirstLine,
          subscriptionColumnIndices
        );
      } catch {
        hasNegative = scanRowsForNegativeValues(
          state.fieldMappings.previewRows || [],
          state.skipFirstLine,
          subscriptionColumnIndices
        );
      }
    } else {
      hasNegative = scanRowsForNegativeValues(
        state.fieldMappings.previewRows || [],
        state.skipFirstLine,
        subscriptionColumnIndices
      );
    }

    commit("setHasNegativeSubscriptionValues", hasNegative);
    commit("setLastNegativeDetectionMappingHash", currentMappingHash);
  },
  async validateAndSuggestMapping({ commit, state }) {
    if (state.fieldMappings.validatedFileChecksum === state.checksum) {
      return;
    }
    if (!state.fieldMappings.enableAutoMapping) {
      commit("setAiProcessed", false);
      return;
    }
    commit("setMappingPreviewLoading", true);
    commit("setMappingSuggestionError", false);
    if (!state.fieldMappings.previewRows || state.fieldMappings.previewRows.length === 0) {
      commit("setMappingPreviewLoading", false);
      commit("setAiProcessed", false);
      return;
    }
    try {
      const firstLines = state.fieldMappings.previewRows
        .slice(0, 10)
        .map((row) => row.join(state.delimiter))
        .join("\n");
      const formData = new FormData();
      formData.append("firstLines", firstLines);
      const response = await axios.post("/import/validate-and-suggest-mapping", formData);
      if (response.data.success) {
        commit("setAiProcessed", true);
        const processedLines = response.data.processedCsv.split("\n").filter((line) => line.trim());
        const processedRows = processedLines.map((line) => {
          return line.split(";");
        });
        if (processedRows.length > 0) {
          const headers = processedRows[0];
          const suggestedMapping = headers.map((header) => {
            return ImportFieldType[header.toUpperCase()] || ImportFieldType.IGNORE;
          });
          commit("setValidatedPreviewRows", {
            rows: processedRows,
            validationInfo: {
              validRowCount: response.data.validRowCount,
              invalidRowCount: response.data.invalidRowCount,
              autoCorrections: response.data.autoCorrections,
              validationErrors: response.data.validationErrors,
            },
            suggestedMapping: suggestedMapping,
          });
          commit("setSkipFirstLine", true);
          if (response.data.hasNegativeSubscriptionValues) {
            commit("setHasNegativeSubscriptionValues", true);
            const mappingHash = JSON.stringify({
              selectedFields: suggestedMapping,
              skipFirstLine: state.skipFirstLine,
              fileChecksum: state.checksum,
            });
            commit("setLastNegativeDetectionMappingHash", mappingHash);
          }
          commit("setSubscriptionSource", determineSubscriptionSource(suggestedMapping));
        } else {
          commit("setMappingSuggestionError", true);
          commit("setAiProcessed", false);
        }
      } else {
        commit("setMappingSuggestionError", true);
        commit("setAiProcessed", false);
      }
    } catch (error) {
      commit("setMappingSuggestionError", true);
      commit("setAiProcessed", false);
    } finally {
      commit("setMappingPreviewLoading", false);
      commit("setValidatedFileChecksum", state.checksum);
    }
  },
};
